<script setup>
import { ref } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import HostPortalLayout from '@/Layouts/HostPortalLayout.vue'
import BlackoutModal from '@/Components/HostPortal/BlackoutModal.vue'

defineOptions({ layout: HostPortalLayout })

const { t } = useI18n()
const props = defineProps({ property: { type: Object, required: true } })

const activeIdx = ref(0)
const photos = props.property.photo_urls?.length ? props.property.photo_urls : []
const showBlackoutModal = ref(false)

function clearBlackout(b) {
  if (!b.owned_by_me) return
  if (!confirm(t('host_portal.property.clear_blackout_confirm'))) return
  router.delete(`/properties/${props.property.id}/blackouts/${b.id}`)
}
</script>

<template>
  <Link
    href="/"
    class="back"
  >
    ← {{ t('host_portal.property.back') }}
  </Link>
  <div class="title-row">
    <h1 class="title">
      {{ property.title }}
    </h1>
    <Link
      :href="`/properties/${property.id}/edit`"
      class="edit-btn"
    >
      {{ t('host_portal.property.edit') }}
    </Link>
  </div>

  <div
    v-if="property.approval_state !== 'approved'"
    class="approval-pill"
    :class="`approval-pill--${property.approval_state}`"
  >
    {{ t(`host_portal.property.approval.${property.approval_state}`) }}
    <span
      v-if="property.rejection_reason"
      class="approval-pill__reason"
    >— {{ property.rejection_reason }}</span>
  </div>

  <div class="layout">
    <div class="gallery">
      <div class="gallery__main">
        <img
          v-if="photos[activeIdx]"
          :src="photos[activeIdx]"
          :alt="property.title"
        >
        <div
          v-else
          class="gallery__placeholder"
        >
          {{ property.title?.[0] }}
        </div>
      </div>
      <ul
        v-if="photos.length > 1"
        class="thumbs"
      >
        <li
          v-for="(p, i) in photos"
          :key="p"
        >
          <button
            :class="['thumb', i === activeIdx && 'is-active']"
            @click="activeIdx = i"
          >
            <img
              :src="p"
              alt=""
            >
          </button>
        </li>
      </ul>
    </div>

    <div class="info">
      <section class="card">
        <h2>{{ t('host_portal.property.sections.details') }}</h2>
        <dl class="detail-list">
          <div class="detail-row">
            <dt>{{ t('host_portal.property_form.fields.category') }}</dt><dd>{{ t(`host_portal.property.categories.${property.category}`) }}</dd>
          </div>
          <div
            v-if="property.area"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.area') }}</dt><dd>{{ property.area }}</dd>
          </div>
          <div
            v-if="property.bedrooms"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.bedrooms') }}</dt><dd>{{ property.bedrooms }}</dd>
          </div>
          <div
            v-if="property.bathrooms"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.bathrooms') }}</dt><dd>{{ property.bathrooms }}</dd>
          </div>
          <div
            v-if="property.max_guests"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.max_guests') }}</dt><dd>{{ property.max_guests }}</dd>
          </div>
          <div
            v-if="property.size_sqm"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.size_sqm') }}</dt><dd>{{ property.size_sqm }}</dd>
          </div>
          <div
            v-if="property.price_per_night"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.price_per_night') }}</dt><dd class="fw-value">
              {{ property.price_per_night }}
            </dd>
          </div>
          <div
            v-if="property.price_per_month"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.price_per_month') }}</dt><dd class="fw-value">
              {{ property.price_per_month }}
            </dd>
          </div>
          <div
            v-if="property.amenities && property.amenities.length > 0"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.amenities') }}</dt>
            <dd>{{ property.amenities.map(a => t(`host_portal.property.amenities.${a}`)).join(' · ') }}</dd>
          </div>
          <div
            v-if="property.address"
            class="detail-row"
          >
            <dt>{{ t('host_portal.property_form.fields.address') }}</dt><dd>{{ property.address }}</dd>
          </div>
        </dl>
      </section>

      <section
        v-if="property.description"
        class="card"
      >
        <h2>{{ t('host_portal.property.sections.description') }}</h2>
        <p class="prose">
          {{ property.description }}
        </p>
      </section>

      <section
        v-if="property.summary"
        class="card"
      >
        <h2>{{ t('host_portal.property.sections.summary') }}</h2>
        <p class="prose">
          {{ property.summary }}
        </p>
      </section>

      <section class="card">
        <div class="card__header">
          <h2>{{ t('host_portal.property.sections.blackouts') }}</h2>
          <button
            class="btn"
            @click="showBlackoutModal = true"
          >
            {{ t('host_portal.property.add_blackout') }}
          </button>
        </div>
        <ul class="blocks">
          <li
            v-for="b in property.blackouts"
            :key="b.id"
            class="block-row"
          >
            {{ b.start_on }} → {{ b.end_on }}
            <span
              v-if="b.reason"
              class="muted"
            >· {{ b.reason }}</span>
            <span
              v-if="b.cleared_at"
              class="muted"
            >· {{ t('host_portal.property.cleared') }}</span>
            <button
              v-if="b.owned_by_me && !b.cleared_at"
              class="link"
              @click="clearBlackout(b)"
            >
              {{ t('host_portal.property.clear') }}
            </button>
          </li>
          <li
            v-if="property.blackouts.length === 0"
            class="empty"
          >
            {{ t('host_portal.property.no_blackouts') }}
          </li>
        </ul>
        <BlackoutModal
          v-if="showBlackoutModal"
          :property="property"
          @close="showBlackoutModal = false"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
.back  { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.title-row { display: flex; align-items: center; gap: 1rem; margin: 0.5rem 0 1.25rem; }
.title { font-family: var(--font-display, var(--display)); font-size: 1.5rem; color: var(--ink); margin: 0; flex: 1; }
.edit-btn { padding: 0.4rem 0.85rem; border: 1px solid var(--line); border-radius: var(--radius-button, 8px); color: var(--ink); text-decoration: none; font-size: 0.875rem; font-weight: 500; }
.edit-btn:hover { border-color: var(--accent); color: var(--accent); }
.approval-pill { display: inline-block; padding: 0.4rem 0.75rem; margin: 0 0 1rem; border-radius: 999px; font-size: 0.8125rem; font-weight: 500; }
.approval-pill--pending  { background: color-mix(in oklch, var(--warn) 16%, var(--surface)); color: var(--warn); }
.approval-pill--rejected { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.approval-pill__reason { font-weight: 400; }
.layout { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.1fr 1fr; gap: 2rem; } }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 0.75rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.gallery__placeholder { font-size: 4rem; font-weight: 700; color: var(--muted); }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 4.5rem; height: 3.5rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1rem 1.25rem; }
.card__header { display: flex; justify-content: space-between; align-items: center; }
.card h2 { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.5rem; }
.detail-list { display: grid; gap: 0.5rem; margin: 0; }
.detail-row { display: grid; grid-template-columns: 10rem 1fr; gap: 0.5rem; align-items: baseline; }
.detail-row dt { font-size: 0.8125rem; color: var(--muted); }
.detail-row dd { font-size: 0.9375rem; color: var(--ink); margin: 0; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.btn { padding: 0.35rem 0.7rem; border: 1px solid var(--line); background: var(--surfaceAlt); color: var(--ink); border-radius: var(--radius-button, 8px); cursor: pointer; font-size: 0.8125rem; }
.blocks { list-style: none; padding: 0; margin: 0; }
.block-row { padding: 0.4rem 0; border-bottom: 1px solid var(--line); font-size: 0.875rem; }
.block-row:last-child { border-bottom: 0; }
.muted { color: var(--muted); font-size: 0.8125rem; margin-left: 0.4rem; }
.link { background: none; border: 0; color: var(--accent); cursor: pointer; font-size: 0.8125rem; margin-left: 0.5rem; }
.empty { color: var(--muted); padding: 0.5rem 0; }
</style>
